import axios from 'axios';
import merge from 'lodash/merge';
import { assert } from '../../utils/assert';

import Loading from '../components/loading.vue';
import Empty from '../components/empty.vue';
import Error from '../components/error.vue';

const components = {};
const sharedData = {};

/**
 * Gets the configuration for the given type.
 *
 * @param key
 */
export const getConfig = key => components[key];

/**
 * Indicates whether or not support for the given content type
 * has been registered for the current user.
 *
 * @param key
 * @return {boolean}
 */
export const hasConfig = key => getConfig(key) !== undefined;

/**
 * Gets the configuration for all shared data.
 */
export const getSharedData = () => sharedData;

/**
 * Adds a new shared data object.
 *
 * These objects will only be loaded asynchronously
 * and only once they're requested the first time.
 *
 * @param key
 * @param promise
 * @param defaultValue
 */
export function addShared(key, promise, defaultValue) {
  sharedData[key] = {
    promise,
    default: defaultValue,
  };
}

/**
 * Adds a shared object fetches through an API request via axios.
 *
 * @param key
 * @param url
 * @param defaultValue
 * @param processor
 * @return {*}
 */
export function addSharedAxios(key, url, defaultValue, processor = (data => data)) {
  return addShared(
    key,
    resolve => axios.request(url).then(({ data }) => resolve(processor(data))),
    defaultValue,
  );
}

/**
 * Adds support to the flex-stack for the given content type.
 *
 * The additional configuration may contain the following options:
 * - components: An object holding all components to render (default, header, loading, empty, error)
 * - request: A function that can be called to request the data (Must return a promise)
 * - closable [true]: Whether or not the column can be closed by the user
 * - data [<key>]: The name of the prop we send to the rendered component
 * - size [1]: Defines how big the column should be in relavitiy to others
 * - [requires]: Defines a properties that this component requires (included via the shared object)
 * - [minWidth]: The minimum panel width (sometimes we want to have this modifiable)
 *
 * @param key The internal key, also used for the routes
 * @param config An additional configuration
 */
export default function addComponent(key, config) {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`Support for ${key} added`); // eslint-disable-line no-console
  }

  // Safety checks
  assert(config.components, `Missing 'components' in configuration for ${key}`);
  assert(config.components.default, `Missing default component for ${key}`);
  assert(config.components.header, `Missing header component for ${key}`);
  assert(config.request, `Missing 'request' in configuration for ${key}`);

  // Add config
  components[key] = merge({
    closable: true,
    data: key,
    version: 1,

    requires: [],
    options: [],
    minWidth: 500,

    components: {
      loading: Loading,
      empty: Empty,
      error: Error,
    },
  }, config);
}



// WEBPACK FOOTER //
// ./resources/assets/js/view-stack/utils/support.js